import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { BasePage, PageType } from 'src/components/BasePage';
import { Empty } from 'src/components/Empty/Empty';
import { Pagination } from 'src/components/Pagination';
import { SpinnerFullWidth } from 'src/components/Spinner/SpinnerFullWidth';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { Entity } from 'src/utils/mobx/BasicStore/BasicStore.types';
import { CombinedLoadingStatus } from 'src/utils/mobx/CombinedLoadingStatus';
import styled from 'styled-components';

type ListPageProps = {
    pageName: string;
    store: BasicStore<Entity>;
    className?: string;
    renderItem: (item: Entity) => React.ReactNode;
    header?: React.ReactNode;
    empty?: React.ReactNode;
};

export const ListPage = observer(
    ({
        pageName,
        store,
        className,
        renderItem,
        header,
        empty = <Empty />,
        ...props
    }: ListPageProps) => {
        useEffect(() => {
            store.loadList();

            return () => {
                store.listLoader.reset();
            };
        }, []);

        const loadingStatus = useMemo(() => {
            return new CombinedLoadingStatus([store.listLoader]);
        }, []);

        if (loadingStatus.isLoading) {
            return <SpinnerFullWidth />;
        }

        const showEmptyMessage =
            !loadingStatus.isLoading && store.list.length === 0;

        return (
            <BasePage
                className={classNames('list-page', className)}
                data-testid="list-page"
                pageName={pageName}
                type={PageType.BASE}
                {...props}
            >
                {header && (
                    <StyledListPageHeader>{header}</StyledListPageHeader>
                )}
                {showEmptyMessage && empty}
                <StyledListItems className="list-page__items">
                    {store.list.map((item) => renderItem(item))}
                </StyledListItems>
                <Pagination store={store} />
            </BasePage>
        );
    },
);

const StyledListItems = styled.div`
    position: relative;
    margin-bottom: 1rem;
`;

const StyledListPageHeader = styled.div`
    position: relative;
    margin-bottom: 2rem;

    h3 {
        margin-bottom: 0;
    }
`;
